import { Fragment, useState, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalHeader,
  Tile,
} from "@carbon/react";
import { WarningAlt } from "@carbon/icons-react";
import {
  Loader,
  ContactInformationCard,
  Chat,
  ClosedBiddingNotice,
} from "../../components";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import { useID } from "../../hooks";
import { useBiddingClosed } from "../../hooks/useBiddingClosed";
import {
  useCreateChat,
  useCreateMessage,
  useOrderChat,
} from "../../hooks/useChat";
import {
  useOffer,
  useUpdateInverseOffer,
  useUpdateOfferDetails,
  useDeclineOffer,
  useUndeclineOffer,
} from "../../hooks/useOffers";
import { queryKeys } from "../../hooks/queryKeys";
import BidResultTable from "./BidResultTable";
import BuyerConditionsPanel from "../Offer/BuyerConditionsPanel";
import SellerOfferHeader from "./SellerOfferHeader";
import SellerOfferConditions from "./SellerOfferConditions";
import SellerOfferLine from "./SellerOfferLine";
import SellerOfferActions from "./SellerOfferActions";
import {
  allLinesPriced,
  calculateMinimumDecrement,
  currencyFormatter,
  LinePriceInputs,
  linePriceInputsOf,
  linePricesPayload,
  offerCurrencyOf,
  pricedLinesOf,
} from "../../utils";
import LinePriceTable from "../../components/LinePriceTable/LinePriceTable";
import { ORDER_TYPES, OrderState } from "../../types";
import { useNotification } from "../../context";
import InverseBidNotice from "./InverseBidNotice";
import "./bid.scss";
import ServicesToQuoteCard from "../../components/ServicesToQuoteCard/ServicesToQuoteCard";
import { servicesToQuoteAboveTheQuote } from "../Order/serviceDetail";
import ServiceAsked from "../../components/ServiceAsked/ServiceAsked";

const actions = {
  UPDATE_OFFER_PRICE: "UPDATE_OFFER_PRICE",
};

const IsSellerOffer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useID();
  const { openNotification, openError } = useNotification();
  const [openChat, setOpenChat] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPrice, setEditPrice] = useState<string>("");
  const [editCurrency, setEditCurrency] = useState<string>("");
  const [editLinePrices, setEditLinePrices] = useState<LinePriceInputs>({});
  const [openDeclineConfirm, setOpenDeclineConfirm] = useState(false);

  const [event, updateEvent] = useReducer(
    (state: any, action: any) => {
      const newState = { ...state };
      switch (action.type) {
        case actions.UPDATE_OFFER_PRICE: {
          const price = parseFloat(action.value);
          if (isNaN(price) || price === 0) {
            newState.offer = null;
            delete newState.errors[action.offerId];
            return newState;
          }

          if (price > parseFloat(action.minDecrement) || price < 0) {
            newState.errors[action.offerId] =
              t("tenders.inverse.minimumDecrement", {
                minPrice: action.minDecrement,
                decrement: action.orderMinimumDecrement || 0,
              }) ||
              `Price must be at least ${action.orderMinimumDecrement}% less than current price. Minimum price: ${action.minDecrement}`;
          } else {
            delete newState.errors[action.offerId];
            newState.offer = { offerId: action.offerId, price: price };
          }
          return newState;
        }
      }
    },
    { offer: null, errors: {} }
  );

  const { data: offer, isLoading: isLoadingOffers, isError } = useOffer(id);

  const { data: chat, isLoading: isLoadingChat } = useOrderChat(
    offer?.order?.id || ""
  );

  const { mutate: mutateChat, isPending: isLoadingCreateChat } = useCreateChat([
    queryKeys.CHAT,
  ]);

  const { mutate: mutateMessage, isPending: isLoadingMessage } =
    useCreateMessage([queryKeys.CHAT]);

  const { mutate: updateInverseOffer, isPending: isUpdating } =
    useUpdateInverseOffer(
      () => openNotification(t("tenders.seller.modifiedOffer")),
      (error) => openError(error)
    );

  const { mutate: updateOfferDetails, isPending: isUpdatingDetails } =
    useUpdateOfferDetails(
      () => {
        openNotification(t("tenders.seller.modifiedOffer"));
        setIsEditMode(false);
        setEditPrice("");
        setEditCurrency("");
      },
      (error) => openError(error)
    );

  const { mutate: declineOffer, isPending: isDeclining } = useDeclineOffer(
    () => {
      setOpenDeclineConfirm(false);
      openNotification(t("tenders.seller.modifiedOffer"));
    },
    (error) => openError(error)
  );

  const { mutate: undeclineOffer, isPending: isUndeclining } =
    useUndeclineOffer(
      () => openNotification(t("tenders.seller.modifiedOffer")),
      (error) => openError(error)
    );

  // Open means taking offers: OPEN and its closing time not yet reached.
  const biddingClosed = useBiddingClosed(offer?.order);
  const isOpen = offer?.order?.state === OrderState.OPEN && !biddingClosed;

  const onSubmitUpdate = () => {
    if (event?.offer && isOpen) {
      updateInverseOffer({
        offerId: event.offer.offerId,
        price: event.offer.price,
      });
    }
  };

  // SCRUM-116: a service quoted by line is re-priced line by line; the backend recomputes the total.
  const pricedLines = pricedLinesOf(offer?.order);
  const pricedByLine = pricedLines.length > 0;

  const onSubmitNormalUpdate = () => {
    if (!offer || !isOpen || offer?.selected) return;
    if (pricedByLine) {
      if (!allLinesPriced(pricedLines, editLinePrices)) return;
      updateOfferDetails({
        offerId: offer.id,
        currency: offerCurrencyOf(pricedLines, editLinePrices),
        linePrices: linePricesPayload(pricedLines, editLinePrices),
      });
    } else if (editPrice && editCurrency) {
      updateOfferDetails({
        offerId: offer.id,
        price: parseFloat(editPrice),
        currency: editCurrency,
      });
    }
  };

  const isNormalOrder = offer?.order?.type === ORDER_TYPES.NORMAL;
  const isInverseOpen =
    offer?.order?.type === ORDER_TYPES.INVERSE &&
    isOpen;
  const isDeclined = offer?.declined === true;
  const canEditNormalOffer =
    isNormalOrder &&
    isOpen &&
    !offer?.selected &&
    !isDeclined;

  const canShowDeclineToggle =
    !!offer &&
    offer.order?.state !== OrderState.CANCELLED &&
    offer.order?.state !== OrderState.SUCCEED &&
    offer.order?.state !== OrderState.CLOSED &&
    !biddingClosed;

  if (
    isLoadingOffers ||
    isUpdating ||
    isUpdatingDetails ||
    isDeclining ||
    isUndeclining
  )
    return <Loader isFullScreen />;

  if (isError) navigate("/not-found");

  if (!offer) return null;

  const startEdit = () => {
    setIsEditMode(true);
    setEditPrice(String(offer?.price || ""));
    setEditCurrency(offer?.currency || "");
    setEditLinePrices(
      linePriceInputsOf(pricedLines, offer?.linePrices, offer?.currency)
    );
  };

  const inversePlaceholder = calculateMinimumDecrement(
    offer?.price,
    offer?.order?.minimumDecrement
  );

  const updateDisabled =
    Object.keys(event.errors).length > 0 ||
    !event.offer ||
    isUpdating ||
    !isOpen ||
    isDeclined;

  const saveDisabled =
    (pricedByLine
      ? !allLinesPriced(pricedLines, editLinePrices)
      : !editPrice || parseFloat(editPrice) <= 0 || !editCurrency) ||
    isUpdatingDetails ||
    !isOpen ||
    offer?.selected ||
    isDeclined;

  return (
    <Fragment>
      <div className="seller-offer__page">
        <SellerOfferHeader
          order={offer.order}
          orderName={offer.order?.name}
          selected={offer.selected}
          chatCount={chat?.messages?.length || 0}
          onOpenChat={() => setOpenChat(true)}
          canDecline={canShowDeclineToggle && !isDeclined}
          isDeclining={isDeclining}
          onDecline={() => setOpenDeclineConfirm(true)}
        />

        {offer.selected && (
          <>
            <BidResultTable
              offer={offer}
              title={t("tenders.seller.accepted")}
            />
            <ContactInformationCard user={offer.order.user} />
          </>
        )}

        <ServicesToQuoteCard
          services={servicesToQuoteAboveTheQuote([offer.order])}
        />

        <div className="seller-offer__panels">
          <BuyerConditionsPanel order={offer.order} itemCount={1} />
          <SellerOfferConditions offer={offer} />
        </div>

        {isDeclined && (
          <div className="seller-offer__decline-banner">
            <p>{t("offer.declineOffer.declinedBanner")}</p>
            {canShowDeclineToggle && (
              <Button
                kind="primary"
                size="sm"
                disabled={isUndeclining}
                onClick={() => undeclineOffer(offer.id)}
              >
                {isUndeclining
                  ? t("cta.loading")
                  : t("offer.declineOffer.undo")}
              </Button>
            )}
          </div>
        )}

        {isInverseOpen && !isDeclined && (
          <InverseBidNotice
            decrement={offer?.order?.minimumDecrement}
            maxPrice={
              inversePlaceholder
                ? currencyFormatter(Number(inversePlaceholder), offer?.currency)
                : undefined
            }
          />
        )}

        {/* SCRUM-116: a service priced by line is offered in its line table, not on one row. */}
        {!pricedByLine && (
          <>
            <div className="seller-offer__columns">
              <span>{t("offer.columns.material")}</span>
              <span>{t("offer.columns.quantity")}</span>
              <span>
                {isInverseOpen
                  ? t("offer.columns.yourBid")
                  : t("offer.columns.yourPrice")}
              </span>
              <span>{t("offer.columns.total")}</span>
            </div>

            <SellerOfferLine
              offer={offer}
              isInverse={isInverseOpen}
              isDeclined={isDeclined}
              canEditNormalOffer={canEditNormalOffer}
              isEditing={isEditMode}
              editPrice={editPrice}
              editCurrency={editCurrency}
              inverseError={event.errors[offer?.id]}
              inversePlaceholder={inversePlaceholder}
              onInverseChange={(value) =>
                updateEvent({
                  type: actions.UPDATE_OFFER_PRICE,
                  value,
                  offerId: offer?.id,
                  minDecrement: inversePlaceholder,
                  orderMinimumDecrement: offer?.order?.minimumDecrement,
                })
              }
              onStartEdit={startEdit}
              onEditPriceChange={setEditPrice}
              onEditCurrencyChange={setEditCurrency}
            />
          </>
        )}

        {pricedByLine && (
          <Tile className="seller-offer__service-asked">
            <dl>
              <ServiceAsked order={offer.order} />
            </dl>
          </Tile>
        )}

        {pricedByLine && (
          <LinePriceTable
            onStartEdit={canEditNormalOffer ? startEdit : undefined}
            lines={pricedLines}
            prices={
              isEditMode
                ? editLinePrices
                : linePriceInputsOf(
                    pricedLines,
                    offer.linePrices,
                    offer.currency
                  )
            }
            onPriceChange={
              isEditMode && canEditNormalOffer
                ? (code, unitPrice) =>
                    setEditLinePrices((prev) => ({
                      ...prev,
                      [code]: { ...prev[code], unitPrice },
                    }))
                : undefined
            }
            onCurrencyChange={(code, currency) =>
              setEditLinePrices((prev) => ({
                ...prev,
                [code]: { ...prev[code], currency },
              }))
            }
            showMissing={isEditMode}
            showDetail={false}
          />
        )}

        {biddingClosed && (
          <ClosedBiddingNotice
            closesAt={offer?.order?.closesAt}
            finishDate={offer?.order?.finishDate}
          />
        )}

        {offer?.order?.state === OrderState.CONSULTATION && (
          <div className="seller-offer__notice">
            <WarningAlt size={20} />
            <p>{t("offer.consultationBlocked")}</p>
          </div>
        )}

        <SellerOfferActions
          isInverseOpen={isInverseOpen}
          isEditMode={isEditMode}
          canEditNormalOffer={canEditNormalOffer}
          canShowDeclineToggle={canShowDeclineToggle}
          isDeclined={isDeclined}
          isUpdating={isUpdating}
          isUpdatingDetails={isUpdatingDetails}
          isDeclining={isDeclining}
          updateDisabled={updateDisabled}
          saveDisabled={saveDisabled}
          onOpenDecline={() => setOpenDeclineConfirm(true)}
          onInverseUpdate={onSubmitUpdate}
          onCancelEdit={() => {
            setIsEditMode(false);
            setEditPrice("");
            setEditCurrency("");
          }}
          onSaveEdit={onSubmitNormalUpdate}
        />

        <div className="seller-offer__disclaimer">
          <p>{t("tenders.disclaimer")}</p>
        </div>
      </div>

      <ComposedModal
        open={openChat}
        onClose={() => setOpenChat(false)}
        size="md"
      >
        <ModalHeader title={t("chat.title")} />
        <ModalBody hasForm>
          <Chat
            isOrder
            chat={chat}
            order={offer?.order}
            onCreateChat={mutateChat}
            isLoadingChat={
              isLoadingChat || isLoadingMessage || isLoadingCreateChat
            }
            onCreateMessage={mutateMessage}
          />
        </ModalBody>
      </ComposedModal>

      <ConfirmDialog
        open={openDeclineConfirm}
        danger
        title={t("offer.declineOffer.confirm.title")}
        body={<p>{t("offer.declineOffer.confirm.body")}</p>}
        confirmLabel={t("offer.declineOffer.confirm.cta")}
        isPending={isDeclining}
        onConfirm={() => declineOffer(offer.id)}
        onCancel={() => setOpenDeclineConfirm(false)}
      />
    </Fragment>
  );
};

export default IsSellerOffer;
